import { Card, Colors, Pagination, Text } from '@amelio/ui-library';
import { useState } from 'react';

import { CommentItem } from './CommentItem';
import { useLinkedThreadHere } from './linkedThread';
import { useUnsentRepliesHere } from './useUnsentReplies';

import type { QuestionComment } from './types';

import { repliesClosedCopy } from '@/constants/review/Feedback360Settings';
import { useAutoScroll } from '@/hooks/useAutoScroll';
import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';

interface CommentSectionProps {
    comments: QuestionComment[];
    onReplySubmit?: (reviewQuestionAnswerId: string, content: string) => Promise<void> | void;
    dataTestId?: string;
    isSubmittingReply?: boolean;
    /** Replies are turned off: said once above the comments, where each "Reply" would have been. */
    areRepliesClosed?: boolean;
    /** Names what replies are turned off for: a 360 feedback, or an evaluation. */
    is360?: boolean;
}

/** A page turned by hand, for the answers it was turned on. */
interface ChosenPage {
    page: number;
    comments: QuestionComment[];
    unsentReplies: Record<string, string>;
}

const PAGE_SIZE = 10;

const unsentReplyTo = (comment: QuestionComment, unsentReplies: Record<string, string>) =>
    comment.reviewQuestionAnswerId ? unsentReplies[comment.reviewQuestionAnswerId] : undefined;

export const CommentSection = ({
    comments,
    onReplySubmit,
    dataTestId = 'comment-section',
    isSubmittingReply = false,
    areRepliesClosed = false,
    is360 = false,
}: CommentSectionProps) => {
    const { t } = useAmelioTranslations();
    const unsentReplies = useUnsentRepliesHere();
    const linkedThreadId = useLinkedThreadHere();

    const [chosenPage, setChosenPage] = useState<ChosenPage | null>(null);

    // Reading the answers again goes back to the first page, unless a refused reply waits on another one, or
    // the link followed names a thread there.
    const unsentReplyIndex = comments.findIndex((comment) => unsentReplyTo(comment, unsentReplies));
    const linkedIndex = comments.findIndex(
        (comment) => comment.reviewQuestionAnswerId === linkedThreadId,
    );
    const heldIndex = unsentReplyIndex < 0 ? linkedIndex : unsentReplyIndex;
    const isChoiceCurrent =
        chosenPage?.comments === comments && chosenPage.unsentReplies === unsentReplies;
    const startingPage = heldIndex < 0 ? 1 : Math.floor(heldIndex / PAGE_SIZE) + 1;
    const currentPage = isChoiceCurrent ? chosenPage.page : startingPage;

    const totalCount = comments.length;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const visibleComments = comments.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const linkedComment = comments[linkedIndex];
    const linkedCommentRef = useAutoScroll<HTMLDivElement>(
        Boolean(linkedComment) && visibleComments.includes(linkedComment),
    );

    if (comments.length === 0) {
        return null;
    }

    return (
        <Card
            className={'mt-6 flex flex-col items-center'}
            padding={'sm'}
            borderRadius={'2xl'}
        >
            {areRepliesClosed && (
                <Text
                    size={'small'}
                    color={Colors.BASE_600}
                    className={'w-full px-2'}
                >
                    {t(repliesClosedCopy(is360))}
                </Text>
            )}

            <div className={'mt-2 w-full space-y-3 overflow-hidden rounded-[15px]'}>
                {visibleComments.map((comment: QuestionComment) => (
                    <div
                        key={comment.id}
                        ref={comment === linkedComment ? linkedCommentRef : undefined}
                        className={'scroll-mt-24'} // clears the host's fixed header
                    >
                        <CommentItem
                            comment={comment}
                            dataTestId={`${dataTestId}-comment-${comment.id}`}
                            onReplySubmit={areRepliesClosed ? undefined : onReplySubmit}
                            unsentReply={
                                areRepliesClosed ? unsentReplyTo(comment, unsentReplies) : undefined
                            }
                            isSubmittingReply={isSubmittingReply}
                        />
                    </div>
                ))}

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={PAGE_SIZE}
                    onPageChange={(page) => setChosenPage({ page, comments, unsentReplies })}
                    dataTestId={`${dataTestId}-pagination`}
                    resultsSummaryText={t('common.showingResults', {
                        start: (currentPage - 1) * PAGE_SIZE + 1,
                        end: Math.min(currentPage * PAGE_SIZE, totalCount),
                        total: totalCount,
                    })}
                />
            </div>
        </Card>
    );
};
